/** @jsx jsx */

/** @jsxFrag Fragment */
import { css, jsx } from '@emotion/react';

import { Fragment } from 'react';

import { useNavigate } from 'js/lib/useRouter';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import type {
  MODAL_STATE,
  S12nUpdateInfo,
} from 'bundles/s12n-common/components/s12n-versioning-v2/useFetchS12nVersioningInfo';

import _t from 'i18n!nls/program-home';

const styles = {
  actionButton: css`
    margin-right: var(--cds-spacing-200);
  `,
};

type Props = {
  state: MODAL_STATE;
  s12nUpdateInfo: S12nUpdateInfo;

  update: () => void;
  continueLearning: () => void;
};

const useLabels = (state: MODAL_STATE, name: string, type: string) => {
  const NO_MESSAGES = { title: '', message: '' };
  const labels: { [key in MODAL_STATE]: { title: string; message: string } } = {
    success: {
      title: _t('Successfully updated!'),
      message: _t('You’re ready to check out the latest content for #{name}.', { name }),
    },
    error: {
      title: _t('Something went wrong'),
      message: _t('You can try again or update later through the #{type} description page.', { type }),
    },
    defer_modal: {
      title: _t('Update postponed'),
      message: _t('You can update to the latest version at anytime through the #{type} description page.', { type }),
    },
    updating: {
      title: _t('Updating...'),
      message: _t('This may take a few seconds'),
    },
    hidden: NO_MESSAGES,
    update_info: NO_MESSAGES,
  };

  return labels[state];
};

const NotificationInfo = ({ state, s12nUpdateInfo, continueLearning, update }: Props) => {
  const { title, message } = useLabels(state, s12nUpdateInfo.name, s12nUpdateInfo.type);

  const navigateTo = useNavigate();

  return (
    <>
      <Dialog.HeadingGroup>{title}</Dialog.HeadingGroup>

      <Dialog.Content id="dialog-content">
        <Typography2 variant="bodyPrimary" component="span">
          {message}
        </Typography2>
      </Dialog.Content>

      {state !== 'updating' && (
        <Dialog.Actions>
          {(state === 'defer_modal' || state === 'success') && (
            <Button css={styles.actionButton} size="medium" variant="primary" onClick={continueLearning}>
              {_t('Continue learning')}
            </Button>
          )}

          {state === 'error' && (
            <Button css={styles.actionButton} size="medium" variant="primary" onClick={update}>
              {_t('Retry update')}
            </Button>
          )}

          <Button
            css={styles.actionButton}
            size="medium"
            variant="secondary"
            onClick={() => {
              navigateTo(`${s12nUpdateInfo.url}`);
            }}
          >
            {_t('Go to homepage')}
          </Button>
        </Dialog.Actions>
      )}
    </>
  );
};

export default NotificationInfo;
